import React, { useCallback, useEffect, useState } from 'react';
import { DropdownItemProps, Table } from 'semantic-ui-react';
import { ActionsProps, ColumnDefinition, TableRowNoDataFound } from '../..';
import TableRow from '../TableRow';
import TableHeader from '../TableHeader';
import TableFooter from '../TableFooter';
import { TableDataProps } from '../../interfaces/TableDataProps';
import { OrderBy, useBodyClass } from '../../';
import { COMPONENT_BODY_CLASSNAME } from './constants';
import './GenericTable.less';
import { ToolTipProps } from '../../interfaces/ToolTipConfig';
import TableRowSpinner from '../TableRowSpinner';
import {
  TableInitialSelectedCell,
  TableSelectedCells,
  TableSelectedCellsRange,
} from '../../interfaces/TableSelectedCells';
import clsx from 'clsx';

export interface Props<TData, TField, TDataSubItem>
  extends TableDataProps<TData, TField> {
  getId: (item: TData | TDataSubItem) => string;
  columnDefinitions: ColumnDefinition<TData, TField, any>[];
  className?: string;
  showCheckButton?: 'box' | 'radio' | boolean;
  actions?: ActionsProps<TData>;
  singleSelect?: boolean;
  hidePagination?: boolean;
  toolTip?: ToolTipProps<TData>;
  onRowClick?: (e: React.MouseEvent<HTMLElement>, item: TData) => void;
  showArrowForRowWithSubItems?: boolean;
  showTableHeadForSubItems?: boolean;
  keyNameSubItemsProperty?: string;
  columnDefinitionsSubItem?: ColumnDefinition<TDataSubItem, any, any>[];
  actionsSubItems?: ActionsProps<TDataSubItem>;
  expandableRowContent?: (rowData: TData[]) => React.ReactElement;
  noDataFoundMessage?: string | HTMLElement;
  noDataFoundColSpan?: string;
  showSpinner?: boolean;
  isCompareCheckedItemsById?: boolean;
  isCanSelectCells?: boolean;
  onSelectingCellsFinished?: (
    initialSelectedCell: TableInitialSelectedCell,
    selectedCells: TableSelectedCells
  ) => void;
  singleLine?: boolean;
  fixed?: boolean;
  totalRows?: number;
  showRowsPerPageSelection?: boolean;
  dropDownOptions?: DropdownItemProps[];
  isHeaderFixed?: boolean;
  enableTooltipForDisabledRows?: boolean;
  disableRow?: (item: TData) => boolean;
  isAccordion?: boolean;
}

export const GenericTable = <TData, TField, TDataSubItem = TData>(
  props: Props<TData, TField, TDataSubItem>
) => {
  const [data, setData] = useState<TData[]>(props.data);
  const [fixedSelectedCells, setFixedSelectedCells] =
    useState<TableSelectedCells>();
  const [selectedCells, setSelectedCells] = useState<TableSelectedCells>();
  const [initialSelectedCell, setInitialSelectedCell] =
    useState<TableInitialSelectedCell>();
  const [isSelectingCellsActive, setIsSelectingCellsStarted] = useState(false);
  useBodyClass(COMPONENT_BODY_CLASSNAME);

  const { onCheckChange } = props;

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  useEffect(() => {
    if (!props.isCanSelectCells) {
      setInitialSelectedCell(undefined);
      setSelectedCells(undefined);
      setFixedSelectedCells(undefined);
    }
  }, [props.isCanSelectCells]);

  const handleSort = useCallback(
    (clickedColumn: ColumnDefinition<TData, TField, any>) => {
      props.onSort({
        name: clickedColumn.name,
        accessor: clickedColumn.accessor,
        selector: clickedColumn.selector!, // eslint-disable-line
      });
    },
    [props]
  );

  const checkIsItemChecked = useCallback(
    (item: TData) => {
      if (props.isCompareCheckedItemsById) {
        return props.checkedItems?.some(
          (checkedItem) => props.getId(checkedItem) === props.getId(item)
        );
      }
      return props.checkedItems?.includes(item);
    },
    [props]
  );

  const handleToggleItem = useCallback(
    (checked: boolean, item: TData) => {
      const array = Array.from(props.checkedItems ?? []);
      if (checked) {
        if (!props.singleSelect) {
          onCheckChange?.([...array, item]);
        } else {
          onCheckChange?.([item]);
        }
        return;
      }
      if (!props.singleSelect) {
        const index = props.isCompareCheckedItemsById
          ? array.findIndex(
              (arrItem) => props.getId(arrItem) === props.getId(item)
            )
          : array.indexOf(item);
        if (index === 0 || (index && index > -1)) {
          array.splice(index, 1);
        }
        onCheckChange?.([...array]);
      } else {
        onCheckChange?.([]);
      }
    },
    [onCheckChange, props]
  );

  const handleSetInitialSelectedCell = useCallback(
    (rowPosition: number, columnPosition: number) => {
      if (!props.isCanSelectCells) return;

      const initialSelectedCells: TableSelectedCells = {
        rowPosition: {
          from: rowPosition,
          to: rowPosition,
        },
        columnPosition: {
          from: columnPosition,
          to: columnPosition,
        },
      };
      setInitialSelectedCell({ rowPosition, columnPosition });
      setFixedSelectedCells(initialSelectedCells);
      setSelectedCells(initialSelectedCells);
    },
    [props.isCanSelectCells]
  );

  const handleSelectingCellsStarted = useCallback(
    () => setIsSelectingCellsStarted(true),
    []
  );

  const handleSelectingCellsFinished = useCallback(() => {
    setIsSelectingCellsStarted(false);
    if (!selectedCells || !initialSelectedCell) return;
    setFixedSelectedCells({ ...selectedCells });
    props.onSelectingCellsFinished?.(initialSelectedCell, selectedCells);
  }, [initialSelectedCell, props, selectedCells]);

  const handleHoverCell = useCallback(
    (rowPosition: number, columnPosition: number) => {
      if (
        !props.isCanSelectCells ||
        !isSelectingCellsActive ||
        !selectedCells ||
        !fixedSelectedCells
      )
        return;
      const newSelectedCellsColumnRange: TableSelectedCellsRange =
        columnPosition < fixedSelectedCells.columnPosition.from
          ? {
              from: columnPosition,
              to: fixedSelectedCells.columnPosition.to,
            }
          : {
              from: fixedSelectedCells.columnPosition.from,
              to: Math.max(
                fixedSelectedCells.columnPosition.to,
                columnPosition
              ),
            };
      const newSelectedCellsRowRange: TableSelectedCellsRange =
        rowPosition < fixedSelectedCells.rowPosition.from
          ? {
              from: rowPosition,
              to: fixedSelectedCells.rowPosition.to,
            }
          : {
              from: fixedSelectedCells.rowPosition.from,
              to: Math.max(fixedSelectedCells.rowPosition.to, rowPosition),
            };
      setSelectedCells({
        rowPosition: newSelectedCellsRowRange,
        columnPosition: newSelectedCellsColumnRange,
      });
    },
    [
      fixedSelectedCells,
      isSelectingCellsActive,
      props.isCanSelectCells,
      selectedCells,
    ]
  );

  const disabled = useCallback(
    (item: TData | any) =>
      (item['disabled'] || props.disableRow?.(item)) ?? false,
    [props]
  );

  const handleTooltip = useCallback(
    (rowData: TData, tooltipData?: ToolTipProps<TData>) => {
      if (props.enableTooltipForDisabledRows && tooltipData) {
        return { ...tooltipData, show: disabled(rowData) !== false };
      }
      return tooltipData;
    },
    [disabled, props.enableTooltipForDisabledRows]
  );

  const [tableAccordionActiveItem, setTableAccordionActiveItem] = useState<
    string | null
  >(null);

  return (
    <Table
      singleLine={props.singleLine}
      fixed={props.fixed}
      selectable
      sortable
      className={clsx(props.className, {
        'p-os-fixed-header': props.isHeaderFixed,
      })}
    >
      <TableHeader
        singleSelect={props.singleSelect}
        columnDefinitions={props.columnDefinitions}
        handleSort={handleSort}
        sortState={props.sortState}
        showCheckButton={props.showCheckButton}
        showActionColumn={!!props.actions}
        checkedItemsLength={
          data.filter((s) =>
            props.checkedItems?.find((a) => props.getId(a) === props.getId(s))
          ).length
        }
        allChecked={
          data.length > 0 &&
          data.filter((s) =>
            props.checkedItems?.find((a) => props.getId(a) === props.getId(s))
          ).length === data.length
        }
        onCheckToggle={(checked: boolean) => {
          if (!props.singleSelect) {
            if (checked) {
              const array = Array.from(props.checkedItems ?? []);
              data.forEach((element) => {
                const index = array.findIndex(
                  (x) => props.getId(x) === props.getId(element)
                );
                if (index && index === -1) array.push(element);
              });
              onCheckChange?.(array);
            } else {
              data.forEach((element) => {
                const index = props.checkedItems?.findIndex(
                  (y) => props.getId(y) === props.getId(element)
                );
                if (index === 0 || (index && index > -1))
                  props.checkedItems?.splice(index, 1);
              });
              onCheckChange?.(Array.from(props.checkedItems ?? []));
            }
          } else {
            setData(OrderBy(data, (x) => props.checkedItems?.includes(x)));
          }
        }}
        showArrowForRowWithSubItems={props.showArrowForRowWithSubItems}
      />
      <Table.Body>
        {data.length === 0 && !props.showSpinner && (
          <TableRowNoDataFound
            colSpan={props.noDataFoundColSpan ?? '6'}
            errorText={props.noDataFoundMessage ?? ''}
          />
        )}
        {props.showSpinner && (
          <TableRowSpinner colSpan={props.noDataFoundColSpan ?? '6'} />
        )}
        {data.map((item, index) => (
          <TableRow
            key={props.getId(item)}
            item={item}
            columnDefinitions={props.columnDefinitions}
            getId={props.getId}
            checked={checkIsItemChecked(item)}
            checkedItems={props.checkedItems?.length}
            toolTip={handleTooltip(item, props.toolTip)}
            onRowClick={props.onRowClick}
            onToggle={(checked) => handleToggleItem(checked, item)}
            showCheckButton={props.showCheckButton}
            actions={props.actions}
            showArrowForRowWithSubItems={props.showArrowForRowWithSubItems}
            keyNameSubItemsProperty={props.keyNameSubItemsProperty}
            columnDefinitionsSubItem={props.columnDefinitionsSubItem}
            expandableRowContent={props.expandableRowContent}
            tableAccordionActiveItem={tableAccordionActiveItem}
            toggleAccordion={setTableAccordionActiveItem}
            actionsSubItems={props.actionsSubItems}
            showTableHeadForSubItems={props.showTableHeadForSubItems}
            rowPosition={index}
            isSelectingCellsActive={isSelectingCellsActive}
            fixedSelectedCells={fixedSelectedCells}
            selectedCells={selectedCells}
            onSetInitialSelectedCell={handleSetInitialSelectedCell}
            onSelectingCellsStarted={handleSelectingCellsStarted}
            onSelectingCellsFinished={handleSelectingCellsFinished}
            onHoverCell={handleHoverCell}
            disabled={disabled(item)}
          />
        ))}
      </Table.Body>
      {!props.hidePagination && !!data.length && (
        <TableFooter
          columns={
            props.columnDefinitions.length +
            +(props.actions != undefined) +
            +(props.showCheckButton != undefined) +
            +(props.showArrowForRowWithSubItems != undefined)
          }
          pageCount={props.pageCount}
          pageIndex={props.pageIndex}
          onPageChange={props.onPageChange}
          onPageSizeChange={props.onPageSizeChange}
          totalRows={props.totalRows}
          showRowsPerPageSelection={props.showRowsPerPageSelection}
          dropDownOptions={props.dropDownOptions}
        />
      )}
    </Table>
  );
};
